import { Button } from './Button';
import { useTranslation } from '../design-system/data/translations';
import type { RuntimeDiagnostic } from '../../application/dto/RuntimeDiagnostic';

export function LoadingState({ label }: { readonly label?: string }) {
  const { t } = useTranslation();
  return (
    <div className="state-panel" role="status">
      <span className="state-panel__pulse" aria-hidden="true" />
      <p>{label ?? t('states.loading')}</p>
    </div>
  );
}

export function ErrorState({
  message,
  onRetry,
  code,
  diagnostic,
}: {
  readonly message: string;
  readonly onRetry: () => void;
  readonly code?: string;
  readonly diagnostic?: RuntimeDiagnostic;
}) {
  const { t } = useTranslation();
  const storageUnavailable = code === 'database_unavailable';
  return (
    <div className="state-panel state-panel--error" role="alert">
      <p className="eyebrow">
        {storageUnavailable ? t('states.storage_unavailable') : t('states.prep_interrupted')}
      </p>
      <h2>{t('states.prep_failed')}</h2>
      <p>{message}</p>
      <Button onClick={onRetry}>{t('error.retry')}</Button>
      {diagnostic ? (
        <details className="technical-diagnostic">
          <summary>{t('states.diagnostic')}</summary>
          <dl>
            {Object.entries(diagnostic).map(([label, value]) => (
              <div key={label}>
                <dt>{label}</dt>
                <dd>{String(value)}</dd>
              </div>
            ))}
          </dl>
        </details>
      ) : null}
    </div>
  );
}

export function EmptyState({
  title,
  message,
}: {
  readonly title: string;
  readonly message: string;
}) {
  const { t } = useTranslation();
  return (
    <div className="state-panel">
      <p className="eyebrow">{t('states.empty_eyebrow')}</p>
      <h2>{title}</h2>
      <p>{message}</p>
    </div>
  );
}
